import React, { FC, useEffect, useRef, useState } from "react"
import { Button, Input, Modal } from "antd"
import type { InputRef } from "antd"

type Props = {
  open: boolean

  /** Header text for the modal. */
  title?: string

  /** Body copy shown above the password input. */
  description?: string

  /** Label for the submit button. */
  submitLabel?: string

  /** Called with the trimmed password value on submit. */
  onSubmit: (password: string) => void
  onCancel: () => void
}

/**
 * Shared admin password popup. Used by both `native-games-control` (unlock
 * add/edit/delete on games) and the `accountsBuffPlay` detail page (delete
 * fingerprint, soft-delete account, etc).
 *
 * Behaviours:
 * - The password input auto-focuses with the caret ready as soon as the modal
 *   opens, so the admin can type immediately.
 * - Pressing Enter submits (`onSubmit` fires with the trimmed value) when the
 *   field is non-empty.
 * - Clearing happens on Cancel as well as on a successful Submit.
 */
export const AdminPasswordModal: FC<Props> = ({
  open,
  title = "Admin Access Required",
  description = "Enter the admin password to continue.",
  submitLabel = "Unlock",
  onSubmit,
  onCancel,
}) => {
  const [password, setPassword] = useState("")
  const inputRef = useRef<InputRef>(null)

  // Focus the input as soon as the modal mounts/opens. Antd renders the modal
  // content asynchronously, so wait one tick to make sure the input is in the
  // DOM before calling .focus().
  useEffect(() => {
    if (!open) return
    const t = setTimeout(() => {
      inputRef.current?.focus({ cursor: "start" })
    }, 0)

    return () => clearTimeout(t)
  }, [open])

  const handleSubmit = () => {
    const trimmed = password.trim()
    if (!trimmed) return
    onSubmit(trimmed)
    setPassword("")
  }

  const handleCancel = () => {
    setPassword("")
    onCancel()
  }

  return (
    <Modal open={open} title={title} onCancel={handleCancel} footer={null}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <p>{description}</p>
        <Input.Password
          ref={inputRef}
          placeholder="Enter password"
          value={password}
          autoComplete="new-password"
          onChange={e => setPassword(e.target.value)}
          onPressEnter={handleSubmit}
        />
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" onClick={handleSubmit} disabled={!password.trim()}>
            {submitLabel}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
